import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import {
  Undo2, Redo2, ArrowLeft, ArrowRight, RefreshCw,
  Pencil, Trash2, History, MousePointerSquareDashed, Copy
} from "lucide-react";
import { useHistoryStore } from "@/stores/history.store";
import { useFileStore } from "@/stores/files.store";
import { useFileOperations } from "@/hooks/use-file-operations";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

interface ToolbarProps {
  onRenameClick?: () => void;
}

export const Toolbar: React.FC<ToolbarProps> = ({ onRenameClick }) => {
  const { canUndo, canRedo, undoCount, undo, redo } = useHistoryStore();
  const leftState = useFileStore(state => state.left);
  const rightState = useFileStore(state => state.right);

  const { transferSelected, mappedTransfer, batchDistribute } = useFileOperations();

  // 映射复制对话框状态
  const [mappedTransferDialogOpen, setMappedTransferDialogOpen] = useState(false);
  const [mappedSubdirectoryName, setMappedSubdirectoryName] = useState("");

  // 批量分发对话框状态
  const [distributeDialogOpen, setDistributeDialogOpen] = useState(false);
  const [subdirectoryName, setSubdirectoryName] = useState("");

  const handleTransferToRight = () => transferSelected('left');
  const handleTransferToLeft = () => transferSelected('right');

  // 映射复制处理
  const handleMappedTransfer = async () => {
    setMappedTransferDialogOpen(false);
    await mappedTransfer(mappedSubdirectoryName.trim());
  };

  // 批量分发处理

  const canTransferToRight = leftState.selectedPaths.length > 0 && !!rightState.path;
  const canTransferToLeft = rightState.selectedPaths.length > 0 && !!leftState.path;
  
  // 映射复制：左边有项目（文件或文件夹），右边有文件夹
  const leftItems = leftState.files;
  const rightDirs = rightState.files.filter(f => f.type === 'directory');
  const canMappedTransfer = leftItems.length > 0 && rightDirs.length > 0;

  // 批量分发：左侧有选中项目，右侧有文件夹
  const canBatchDistribute = leftState.selectedPaths.length > 0 && rightDirs.length > 0;

  const handleBatchDistribute = async () => {
    setDistributeDialogOpen(false);
    // 如果子目录名为空，则传递空字符串，表示直接复制到目标文件夹根目录
    await batchDistribute(subdirectoryName.trim());
  };

  return (
    <TooltipProvider>
      <div className="h-12 border-b flex items-center gap-1 px-4 bg-muted/10">
        {/* 撤回操作组 */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled={!canUndo()}
                onClick={undo}
                className="gap-1 h-8"
              >
                <Undo2 className="w-4 h-4" />
                <span className="hidden sm:inline">撤销</span>
                {undoCount > 0 && <Badge variant="secondary" className="ml-0.5 h-4 px-1 text-[10px]">{undoCount}</Badge>}
              </Button>
            </TooltipTrigger>
            <TooltipContent>撤销 (Ctrl+Z)</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                disabled={!canRedo()}
                onClick={redo}
                className="h-8"
              >
                <Redo2 className="w-4 h-4" />
                <span className="hidden sm:inline">重做</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>重做 (Ctrl+Y)</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* 传输与同步 */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 h-8"
                disabled={!canTransferToLeft}
                onClick={handleTransferToLeft}
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden md:inline">复制到左</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>复制选中项到左侧面板</TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 h-8"
                disabled={!canTransferToRight}
                onClick={handleTransferToRight}
              >
                <ArrowRight className="w-4 h-4" />
                <span className="hidden md:inline">复制到右</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>复制选中项到右侧面板</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 h-8"
                disabled={!canMappedTransfer}
                onClick={() => setMappedTransferDialogOpen(true)}
              >
                <MousePointerSquareDashed className="w-4 h-4" />
                <span className="hidden md:inline">映射复制</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>按行对应：左边项目 → 右边文件夹（可选指定子目录）({Math.min(leftItems.length, rightDirs.length)} 对)</TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 h-8 text-green-600 hover:text-green-700 hover:bg-green-50"
                disabled={!canBatchDistribute}
                onClick={() => setDistributeDialogOpen(true)}
              >
                <Copy className="w-4 h-4" />
                <span className="hidden md:inline">批量分发</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>将左侧选中的文件分发到右侧所有文件夹（可选指定子目录）({leftState.selectedPaths.length} 个文件 → {rightDirs.length} 个文件夹)</TooltipContent>
          </Tooltip>
        </div>

        <Separator orientation="vertical" className="h-6 mx-1" />

        {/* 编辑组 */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 h-8 text-blue-500 hover:text-blue-600 hover:bg-blue-50"
            onClick={onRenameClick}
            disabled={leftState.selectedPaths.length === 0}
          >
            <Pencil className="w-4 h-4" />
            <span className="hidden sm:inline">批量重命名</span>
          </Button>
          <Button variant="ghost" size="sm" className="gap-1 h-8 text-destructive hover:bg-destructive/10">
            <Trash2 className="w-4 h-4" />
            <span className="hidden sm:inline">删除</span>
          </Button>
          <Button variant="ghost" size="sm" className="gap-1 h-8">
            <History className="w-4 h-4" />
            <span className="hidden sm:inline">历史</span>
          </Button>
        </div>
        
        <div className="ml-auto">
          <Button variant="ghost" size="sm" className="h-8" onClick={() => {
            if (leftState.path) useFileStore.getState().refresh('left');
            if (rightState.path) useFileStore.getState().refresh('right');
          }}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* 批量分发对话框 */}
      <Dialog open={distributeDialogOpen} onOpenChange={setDistributeDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>批量分发</DialogTitle>
            <DialogDescription>
              将左侧选中的 <span className="font-semibold text-foreground">{leftState.selectedPaths.length}</span> 个文件，复制到右侧 <span className="font-semibold text-foreground">{rightDirs.length}</span> 个文件夹中。
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label htmlFor="subdir-input" className="text-sm font-medium mb-2 block">
              目标子目录名称（可选）
            </label>
            <Input
              id="subdir-input"
              value={subdirectoryName}
              onChange={(e) => setSubdirectoryName(e.target.value)}
              placeholder="留空则直接复制到目标文件夹根目录"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleBatchDistribute();
                }
              }}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground mt-2">
              {subdirectoryName.trim()
                ? <>文件将被复制到每个目标文件夹的 <span className="font-mono bg-muted px-1 rounded">/{subdirectoryName}</span> 目录下</>
                : <>文件将直接复制到每个目标文件夹的根目录下</>
              }
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDistributeDialogOpen(false)}>
              取消
            </Button>
            <Button
              onClick={handleBatchDistribute}
              className="bg-green-600 hover:bg-green-700"
            >
              开始分发
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 映射复制对话框 */}
      <Dialog open={mappedTransferDialogOpen} onOpenChange={setMappedTransferDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>映射复制</DialogTitle>
            <DialogDescription>
              按行对应复制：左边第 <span className="font-semibold text-foreground">1</span> 个项目 → 右边第 <span className="font-semibold text-foreground">1</span> 个文件夹，左边第 <span className="font-semibold text-foreground">2</span> 个项目 → 右边第 <span className="font-semibold text-foreground">2</span> 个文件夹，依此类推。共 <span className="font-semibold text-foreground">{Math.min(leftItems.length, rightDirs.length)}</span> 对。
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label htmlFor="mapped-subdir-input" className="text-sm font-medium mb-2 block">
              目标子目录名称（可选）
            </label>
            <Input
              id="mapped-subdir-input"
              value={mappedSubdirectoryName}
              onChange={(e) => setMappedSubdirectoryName(e.target.value)}
              placeholder="留空则直接复制到目标文件夹根目录"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleMappedTransfer();
                }
              }}
              className="w-full"
            />
            <p className="text-xs text-muted-foreground mt-2">
              {mappedSubdirectoryName.trim()
                ? <>文件将被复制到每个目标文件夹的 <span className="font-mono bg-muted px-1 rounded">/{mappedSubdirectoryName}</span> 目录下</>
                : <>文件将直接复制到每个目标文件夹的根目录下</>
              }
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMappedTransferDialogOpen(false)}>
              取消
            </Button>
            <Button
              onClick={handleMappedTransfer}
              className="bg-blue-600 hover:bg-blue-700"
            >
              开始复制
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};
